// SC-2853 (the FE of BE SC-2753) — build the ambient `canvas_context` snapshot
// that rides beside each chat message, so the agent knows what the user is
// looking at ("what's this finding about?" with a drawer open).
//
// The snapshot is read AT SEND TIME from wherever each piece of view state
// actually lives — the same splits the views themselves use:
//   • view + overlay        → `workspaceStore` (the overlay record is mirrored
//     there by every drawer owner — see `openOverlay`'s doc);
//   • entities params       → `tableViewStore` (store-backed view), wrapped as
//     the same `ViewDirective` the agent's own directives use;
//   • findings / history / attributes params → the URL, via each view's own
//     parser (the inverse of the `*DirectiveToSearch` bridges), so the snapshot
//     can't drift from what the view renders.
//
// Ids + labels only — never row values. Filters are snapshotted at their
// EFFECTIVE values (the findings default becomes an explicit `status:
// ["pending"]`) so the agent reads the truth without knowing FE defaulting
// rules. The BE is fully lenient about this field, so the capture wrapper
// degrades to context-blind on any internal error rather than blocking a send.
//
// ASSUMPTION: every live chat surface sends from inside the catalog workspace
// (`/catalogs/:id/*`), so the store's view + the current URL describe what the
// user actually sees. A chat surface OUTSIDE those routes (e.g. reviving the
// parked AgentTab briefing as a standalone page) would snapshot the catalog's
// LAST canvas state — misleading; such a surface should send no snapshot
// instead. The catalog Home ticket carries the reminder.

import {
  isBoardCanvasView,
  type CanvasContextRef,
  type CanvasContextSnapshot,
  type CanvasDirectiveParams,
  type CanvasView,
} from '../../types';
import {
  EMPTY_WORKSPACE_SLICE,
  useWorkspaceStore,
  type WorkspaceOverlay,
} from '../../store/workspaceStore';
import {
  buildTableQuery,
  EMPTY_TABLE_SLICE,
  useTableViewStore,
  type CatalogTableSlice,
} from '../../store/tableViewStore';
import { parseFilters } from '../catalog/findings/findingFilters';
import { parseSince } from '../changes/historyFilters';
import { isAttributeSortKey, parseAttributeSort } from '../catalog/attributes/attributeSort';

/** The store overlay → a wire ref, or `undefined` for none. Labels are best-effort
 *  (an agent-/URL-opened overlay carries none); attribute refs are the column name
 *  alone. A row overlay (SC-3517) is FE-only: it rides as the SKU entity ref the
 *  agent understands once the row has loaded one, and is left out until then —
 *  never sent as `kind: 'row'`, which the BE's lenient parse would turn into a
 *  null snapshot. On a duplicate SKU the agent may read the other row; accepted,
 *  the same gap every agent tool has. */
function toContextRef(overlay: Exclude<WorkspaceOverlay, null>): CanvasContextRef | undefined {
  if (overlay.kind === 'attribute') return { kind: 'attribute', field: overlay.field };
  if (overlay.kind === 'row') {
    if (!overlay.sku) return undefined;
    return overlay.label !== undefined
      ? { kind: 'entity', id: overlay.sku, label: overlay.label }
      : { kind: 'entity', id: overlay.sku };
  }
  return overlay.label !== undefined
    ? { kind: overlay.kind, id: overlay.id, label: overlay.label }
    : { kind: overlay.kind, id: overlay.id };
}

/** The active view's params in the `CanvasDirectiveParams` vocabulary, or
 *  `undefined` for a view with nothing to say (profile; a bare history/attributes
 *  view). URL-driven views parse the CURRENT location's query with their own
 *  parsers; entities wraps the table store's live query. */
function paramsForView(
  view: CanvasView,
  tableSlice: CatalogTableSlice,
  search: URLSearchParams,
): CanvasDirectiveParams | undefined {
  // A board view (SC-2872) snapshots as its bare `board:<id>` view id — the id
  // names the board, and boards carry no agent-addressable params yet (the
  // viewer's tile params live in the shell overlay; whether they should ride
  // the snapshot is an SC-2874 authoring-loop question).
  if (isBoardCanvasView(view)) return undefined;
  switch (view) {
    case 'entities':
      // The same wrap `applyCanvasDirective` consumes: the live query, with the
      // agent caption (when an agent drove the current view) as the narration.
      return {
        view: 'entities',
        table: {
          query: buildTableQuery(tableSlice),
          mode: 'replace',
          narration: tableSlice.narration ?? '',
        },
      };
    case 'findings': {
      // Effective values, explicitly: the absent-status default parses to
      // `['pending']` and is SENT as that; empty scope/kind arrays are the
      // explicit "unconstrained". Only `minSeverity: 'all'` is omitted — the
      // wire vocabulary has no 'all' (absent IS "no floor").
      const f = parseFilters(search);
      return {
        view: 'findings',
        status: f.status,
        scope: f.scope,
        kind: f.kind,
        field: f.field,
        ...(f.minSeverity !== 'all' ? { minSeverity: f.minSeverity } : {}),
        sort: f.sort,
        sortDir: f.sortDir,
      };
    }
    case 'history': {
      // Only `since` is agent-addressable today (the `action` facet is
      // human-rail-only until BE SC-27xx) — a bare feed sends no params.
      const since = parseSince(search);
      return since ? { view: 'history', since } : undefined;
    }
    case 'attributes': {
      // The sort key IS the wire vocab (attributeSort.ts); direction isn't
      // agent-addressable, so it doesn't ride. Natural order sends no params.
      // The guard re-narrows the generic `Sort.key: string` — parse already
      // only returns the three attribute keys, so this can't actually drop one.
      const sort = parseAttributeSort(search);
      return sort && isAttributeSortKey(sort.key)
        ? { view: 'attributes', sort: sort.key }
        : undefined;
    }
    case 'profile':
      return undefined;
  }
}

/** Pure snapshot assembly — exported for tests; callers use
 *  {@link captureCanvasContext}. */
export function buildCanvasContextSnapshot(
  view: CanvasView,
  overlay: WorkspaceOverlay,
  tableSlice: CatalogTableSlice,
  search: URLSearchParams,
): CanvasContextSnapshot {
  const snapshot: CanvasContextSnapshot = { view };
  const params = paramsForView(view, tableSlice, search);
  if (params) snapshot.params = params;
  const ref = overlay ? toContextRef(overlay) : undefined;
  if (ref) snapshot.overlay = ref;
  // `selection` is deliberately absent: the table has no row selection yet, and
  // per the BE contract bulk gestures should stay filter-shaped anyway.
  return snapshot;
}

/**
 * Read the live stores + location and build the snapshot for `catalogId`, or
 * `undefined` to send context-blind. Never throws: the snapshot is ambient
 * enrichment, so an internal error here must degrade the turn (matching the
 * BE's own leniency about the field), not block the user's message.
 */
export function captureCanvasContext(catalogId: string): CanvasContextSnapshot | undefined {
  try {
    const ws = useWorkspaceStore.getState().byCatalog[catalogId] ?? EMPTY_WORKSPACE_SLICE;
    const table = useTableViewStore.getState().byCatalog[catalogId] ?? EMPTY_TABLE_SLICE;
    return buildCanvasContextSnapshot(
      ws.activeView,
      ws.overlay,
      table,
      new URLSearchParams(window.location.search),
    );
  } catch (e) {
    console.warn('canvas_context snapshot failed; sending context-blind', e);
    return undefined;
  }
}
